import {
  basemapCustomizationFor,
  basemapPreset,
  DEFAULT_BASEMAP_CUSTOMIZATION,
} from '../../basemaps/catalog';
import { applyBasemapPresentation } from '../../basemaps/presentation';
import { DEFAULT_LABEL_APPEARANCE, isBasemapLabelAppearance } from '../../basemaps/labelAppearance';
import {
  applyBasemapLabelPresentation,
  basemapLabelRecipe,
  BASEMAP_LABEL_GROUPS,
} from '../../basemaps/labelPresentation';
import { isCapabilityAvailable } from '../../licensing/entitlements';
import { normaliseMapBackgroundColour } from '../../map/presentation';
import { normaliseHexColour } from '../../styles/colour';
import { ACTION_OK, actionError, type AppStoreActionContext } from './actionSupport';
import type { AppState } from './appState';

export type BasemapViewActions = Pick<
  AppState,
  | 'setBasemapPreset'
  | 'applyBasemapPresentation'
  | 'applyBasemapLabelPresentation'
  | 'setBasemapBuildings3d'
  | 'setBasemapContextOpacity'
  | 'setBasemapLabelsOpacity'
  | 'setBasemapLabelAppearance'
  | 'resetBasemapLabelAppearance'
  | 'setBasemapVisibility'
  | 'setBasemapColour'
  | 'resetBasemapCustomization'
  | 'setMapBackgroundColour'
  | 'updateMapView'
>;

export function createBasemapViewActions({ get, set }: AppStoreActionContext): BasemapViewActions {
  return {
    setBasemapLabelAppearance: (patch) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize'))
        return actionError('Basemap customization requires Placeio Maps Pro.');
      const basemap = state.project.map.basemap;
      const family = basemap.presetId;
      if (family === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const previous = basemap.customizationByPreset[family];
      const labelAppearance = {
        ...(previous.labelAppearance ?? DEFAULT_LABEL_APPEARANCE),
        ...patch,
      };
      if (!isBasemapLabelAppearance(labelAppearance))
        return actionError('Choose valid bounded label appearance values.');
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...basemap,
              customizationByPreset: {
                ...basemap.customizationByPreset,
                [family]: { ...previous, labelAppearance },
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    resetBasemapLabelAppearance: () => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize'))
        return actionError('Basemap customization requires Placeio Maps Pro.');
      const basemap = state.project.map.basemap;
      const family = basemap.presetId;
      if (family === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const customization = { ...basemap.customizationByPreset[family] };
      // Class visibility now belongs to Label content, independently of appearance reset.
      const roadTiers = customization.labelAppearance?.roadTiers;
      if (roadTiers) customization.labelAppearance = { ...DEFAULT_LABEL_APPEARANCE, roadTiers };
      else delete customization.labelAppearance;
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...basemap,
              customizationByPreset: {
                ...basemap.customizationByPreset,
                [family]: customization,
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    applyBasemapLabelPresentation: (presentation) => {
      const state = get();
      if (state.project.map.basemap.presetId === 'none')
        return actionError('Choose a Placeio Maps basemap before applying a label presentation.');
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: applyBasemapLabelPresentation(state.project.map.basemap, presentation),
          },
        },
      });
      return ACTION_OK;
    },
    applyBasemapPresentation: (presentation) => {
      const state = get();
      if (state.project.map.basemap.presetId === 'none') {
        return actionError('Choose a Placeio Maps basemap before applying a presentation.');
      }
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: applyBasemapPresentation(state.project.map.basemap, presentation),
          },
        },
      });
      return ACTION_OK;
    },
    setBasemapPreset: (presetId) => {
      set((state) => ({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              presetId,
              buildings3d: presetId === 'none' ? false : state.project.map.basemap.buildings3d,
            },
          },
        },
      }));
      return ACTION_OK;
    },
    setBasemapContextOpacity: (opacity) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize')) {
        return actionError('Basemap customization requires Placeio Maps Pro.');
      }
      if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
        return actionError('Basemap context opacity must be between 0 and 1.');
      }
      const presetId = state.project.map.basemap.presetId;
      if (presetId === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const customization = basemapCustomizationFor(state.project.map.basemap, presetId);
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              customizationByPreset: {
                ...state.project.map.basemap.customizationByPreset,
                [presetId]: {
                  ...customization,
                  contextOpacity:
                    opacity === basemapPreset(presetId).customizationDefaults.contextOpacity
                      ? null
                      : opacity,
                },
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    setBasemapLabelsOpacity: (opacity) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize')) {
        return actionError('Basemap customization requires Placeio Maps Pro.');
      }
      if (!Number.isFinite(opacity) || opacity < 0 || opacity > 1) {
        return actionError('Basemap labels opacity must be between 0 and 1.');
      }
      const presetId = state.project.map.basemap.presetId;
      if (presetId === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const customization = basemapCustomizationFor(state.project.map.basemap, presetId);
      const intent = state.project.map.basemap.labelPresentation;
      const labelDefaults =
        intent && intent !== 'follow-basemap'
          ? basemapLabelRecipe(presetId, intent).opacity
          : basemapPreset(presetId).customizationDefaults.labelsOpacity;
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              customizationByPreset: {
                ...state.project.map.basemap.customizationByPreset,
                [presetId]: {
                  ...customization,
                  labelsOpacity: opacity === labelDefaults ? null : opacity,
                },
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    setBasemapVisibility: (group, visible) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize')) {
        return actionError('Basemap customization requires Placeio Maps Pro.');
      }
      const presetId = state.project.map.basemap.presetId;
      if (presetId === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const customization = basemapCustomizationFor(state.project.map.basemap, presetId);
      const intent = state.project.map.basemap.labelPresentation;
      const labelGroup = BASEMAP_LABEL_GROUPS.find((label) => label === group);
      const visibilityDefault =
        intent && intent !== 'follow-basemap' && labelGroup
          ? basemapLabelRecipe(presetId, intent).visibility[labelGroup]
          : basemapPreset(presetId).customizationDefaults.visibility[group];
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              customizationByPreset: {
                ...state.project.map.basemap.customizationByPreset,
                [presetId]: {
                  ...customization,
                  ...(group === 'roadNames' && customization.labelAppearance?.roadTiers
                    ? {
                        labelAppearance: {
                          ...customization.labelAppearance,
                          roadTiers: {
                            motorwaysHighways: visible,
                            arterialRoads: visible,
                            collectorRoads: visible,
                            localRoads: visible,
                          },
                        },
                      }
                    : {}),
                  visibility: {
                    ...customization.visibility,
                    [group]: visible === visibilityDefault ? null : visible,
                  },
                },
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    setBasemapColour: (group, colour) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize')) {
        return actionError('Basemap customization requires Placeio Maps Pro.');
      }
      const normalized = colour === null ? null : normaliseHexColour(colour);
      if (colour !== null && !normalized) return actionError('Choose a valid hex colour.');
      const presetId = state.project.map.basemap.presetId;
      if (presetId === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      const customization = basemapCustomizationFor(state.project.map.basemap, presetId);
      const override =
        normalized === basemapPreset(presetId).customizationDefaults.colours[group]
          ? null
          : normalized;
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              customizationByPreset: {
                ...state.project.map.basemap.customizationByPreset,
                [presetId]: {
                  ...customization,
                  colours: {
                    ...customization.colours,
                    [group]: override,
                  },
                },
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    resetBasemapCustomization: (targetPresetId) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.customize')) {
        return actionError('Basemap customization requires Placeio Maps Pro.');
      }
      const presetId = targetPresetId ?? state.project.map.basemap.presetId;
      if (presetId === 'none') return actionError('Choose a Placeio Maps basemap to customise.');
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: {
              ...state.project.map.basemap,
              customizationByPreset: {
                ...state.project.map.basemap.customizationByPreset,
                [presetId]: DEFAULT_BASEMAP_CUSTOMIZATION,
              },
            },
          },
        },
      });
      return ACTION_OK;
    },
    setBasemapBuildings3d: (enabled) => {
      const state = get();
      if (!isCapabilityAvailable(state.entitlement, 'basemap.buildings3d')) {
        return actionError('3D buildings require Placeio Maps Pro.');
      }
      if (state.project.map.basemap.presetId === 'none') {
        return actionError('Choose a Placeio Maps basemap before enabling 3D buildings.');
      }
      set({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            basemap: { ...state.project.map.basemap, buildings3d: enabled },
          },
        },
      });
      return ACTION_OK;
    },
    setMapBackgroundColour: (colour) => {
      const backgroundColour = normaliseMapBackgroundColour(colour);
      if (!backgroundColour) return actionError('Choose a valid hex colour.');
      set((state) => ({
        project: {
          ...state.project,
          map: { ...state.project.map, backgroundColour },
        },
      }));
      return ACTION_OK;
    },
    updateMapView: (view) =>
      set((state) => ({
        project: {
          ...state.project,
          map: {
            ...state.project.map,
            view: {
              ...view,
              pitch: isCapabilityAvailable(state.entitlement, 'map.pitch')
                ? view.pitch
                : state.project.map.view.pitch,
            },
          },
        },
        shell: { ...state.shell, inspectedFeature: null, searchTarget: null },
      })),
  };
}
